import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, User, Phone, Mail, FileText, MapPin, Loader2, Copy, Check, Clock } from "lucide-react";
import { api, formatBRL, formatApiErrorDetail } from "../../lib/api";
import { hourBankName, economia } from "../../lib/hourbank";
import { maskPhoneBR, sanitizePhone } from "../../lib/phone";
import {
  maskCPF, isValidCPF, sanitizeDigits, maskCEP, isValidCEP,
  fetchAddressByCEP, maskRG, BR_STATES,
} from "../../lib/documents";

const INPUT_CLS =
  "w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base bg-white text-[#0A1128] placeholder:text-gray-400 focus:border-[#D4AF37] focus:outline-none";
const LBL_CLS =
  "text-xs uppercase tracking-widest text-gray-600 font-bold flex items-center gap-1.5 mb-2";

export default function ComprarPlano() {
  const { planId } = useParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [form, setForm] = useState({
    guest_name: "", guest_phone: "", guest_email: "",
    guest_cpf: "", guest_rg: "",
    address_cep: "", address_street: "", address_number: "", address_complement: "",
    address_neighborhood: "", address_city: "", address_state: "",
  });
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [method, setMethod] = useState("pix");
  const [purchase, setPurchase] = useState(null); // {invoice_id, pix_qrcode, pix_copy_paste, amount}
  const [invoiceStatus, setInvoiceStatus] = useState("pending");
  const [copied, setCopied] = useState(false);

  // Load plan
  useEffect(() => {
    api.get("/public/plans")
      .then(({ data }) => {
        const p = data.find((x) => x.id === planId);
        if (!p) { toast.error("Plano não encontrado"); nav("/planos"); return; }
        setPlan(p);
      })
      .catch(() => { toast.error("Falha ao carregar plano"); nav("/planos"); });
    api.get("/public/spaces").then(({ data }) => setSpaces(data)).catch(() => {});
  }, [planId, nav]);

  // Poll invoice status once PIX is created
  useEffect(() => {
    if (!purchase?.invoice_id || invoiceStatus === "paid") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/invoices/${purchase.invoice_id}/public-status`);
        setInvoiceStatus(data.status);
        if (data.status === "paid") {
          clearInterval(iv);
          toast.success("Pagamento confirmado! Seu plano foi ativado.");
        }
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [purchase, invoiceStatus]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onCepBlur = async () => {
    if (!isValidCEP(form.address_cep)) return;
    setCepLoading(true);
    const addr = await fetchAddressByCEP(form.address_cep);
    setCepLoading(false);
    if (addr) {
      update({
        address_street: addr.street || form.address_street,
        address_neighborhood: addr.neighborhood || form.address_neighborhood,
        address_city: addr.city || form.address_city,
        address_state: addr.state || form.address_state,
      });
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (loading) return;
    if (!isValidCPF(form.guest_cpf)) return toast.error("CPF inválido.");
    if (!isValidCEP(form.address_cep)) return toast.error("CEP inválido.");
    if (!form.address_state || form.address_state.length !== 2) return toast.error("Selecione o estado.");
    setLoading(true);
    try {
      const { data } = await api.post("/public/plans/purchase", {
        plan_id: planId,
        guest_name: form.guest_name.trim(),
        guest_phone: sanitizePhone(form.guest_phone),
        guest_email: form.guest_email.trim().toLowerCase(),
        guest_cpf: sanitizeDigits(form.guest_cpf),
        guest_rg: form.guest_rg.trim(),
        address_cep: sanitizeDigits(form.address_cep),
        address_street: form.address_street.trim(),
        address_number: form.address_number.trim(),
        address_complement: form.address_complement.trim() || null,
        address_neighborhood: form.address_neighborhood.trim(),
        address_city: form.address_city.trim(),
        address_state: form.address_state.trim().toUpperCase(),
        payment_method: method,
      });
      setPurchase(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Não foi possível gerar o PIX";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(purchase.pix_copy_paste);
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch { toast.error("Falha ao copiar"); }
  };

  if (!plan) return <div className="p-8 text-center text-gray-400">Carregando…</div>;

  // ═══ Screen 2: PIX generated ═══
  if (purchase) {
    return (
      <div className="bg-gray-50 min-h-screen py-8" data-testid="comprar-plano-pix">
        <div className="max-w-lg mx-auto px-4">
          <div className="text-center mb-6">
            {invoiceStatus === "paid" ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                  <Check size={28} className="text-emerald-600" />
                </div>
                <h1 className="font-display text-3xl text-[#0A1128]">Pagamento confirmado!</h1>
                <p className="text-gray-600 mt-2">Seu plano <b>{plan.name}</b> foi ativado. Enviamos a confirmação por WhatsApp.</p>
              </>
            ) : (
              <>
                <h1 className="font-display text-2xl text-[#0A1128]">
                  {method === "pix" ? "Pague com PIX para ativar seu plano" :
                   method === "cartao" ? "Finalize o pagamento com Cartão" :
                   "Pague o Boleto para ativar seu plano"}
                </h1>
                <p className="text-gray-600 mt-1"><b>{plan.name}</b> · {formatBRL(purchase.amount)}</p>
              </>
            )}
          </div>
          {invoiceStatus !== "paid" && (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 space-y-4">
              {purchase.pix_qrcode && (
                <div className="flex justify-center">
                  <img
                    alt="QR Code PIX"
                    src={purchase.pix_qrcode.startsWith("data:") ? purchase.pix_qrcode : `data:image/png;base64,${purchase.pix_qrcode}`}
                    className="w-64 h-64 border-2 border-gray-100 rounded-lg"
                    data-testid="plan-pix-qrcode"
                  />
                </div>
              )}
              {!purchase.pix_qrcode && purchase.payment_link && (
                <a href={purchase.payment_link} target="_blank" rel="noreferrer"
                  data-testid="plan-payment-link"
                  className="block w-full text-center bg-[#0A1128] text-white rounded-xl py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#14213D]">
                  {method === "cartao" ? "Pagar com Cartão (Mercado Pago) →" : "Abrir Boleto →"}
                </a>
              )}
              {purchase.pix_copy_paste && (
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2">PIX Copia e Cola</div>
                <div className="flex gap-2">
                  <input readOnly value={purchase.pix_copy_paste || ""} className={`${INPUT_CLS} text-xs`} data-testid="plan-pix-code" />
                  <button onClick={copyPix} className="px-4 bg-[#0A1128] text-white rounded-xl font-bold text-xs flex items-center gap-1.5" data-testid="plan-pix-copy">
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>
              </div>
              )}
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs">
                <Clock size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-amber-900">
                  Assim que o pagamento cair, seu plano é <b>ativado automaticamente</b> e você recebe a confirmação por WhatsApp e e-mail.
                </div>
              </div>
              <div className="text-center text-xs text-gray-400">
                Status: <span className="uppercase font-bold" data-testid="plan-invoice-status">{invoiceStatus}</span> · atualizando a cada 5s
              </div>
            </div>
          )}
          <div className="mt-6 text-center">
            <Link to="/planos" className="text-sm text-[#0A1128] underline">Voltar aos planos</Link>
          </div>
        </div>
      </div>
    );
  }

  // ═══ Screen 1: Identification form ═══
  const price = plan.price_package || plan.price_monthly || 0;
  return (
    <div className="bg-gray-50 min-h-screen pb-12" data-testid="comprar-plano-form">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/planos" className="flex items-center gap-2 text-sm font-semibold text-[#0A1128]" data-testid="back-to-planos">
            <ArrowLeft size={18} /> Voltar aos planos
          </Link>
          <div className="text-xs uppercase tracking-widest text-gray-500">
            Compra segura via <b className="text-[#D4AF37]">PIX</b>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <form onSubmit={submit} className="space-y-6">
          <header>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-1">{plan.category || "Plano"}</div>
            <h1 className="font-display text-3xl text-[#0A1128]">{plan.pricing_mode === "package" ? hourBankName(plan) : plan.name}</h1>
            <p className="text-gray-600 mt-2">Preencha seus dados para gerar o PIX e ativar seu plano.</p>
          </header>

          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700">Dados pessoais</h2>
            <div>
              <label className={LBL_CLS}><User size={14} /> Nome completo</label>
              <input required minLength={2} value={form.guest_name} onChange={(e) => update({ guest_name: e.target.value })} placeholder="Ex: Maria Silva" className={INPUT_CLS} data-testid="plan-input-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LBL_CLS}><Phone size={14} /> WhatsApp</label>
                <input required type="tel" value={form.guest_phone} onChange={(e) => update({ guest_phone: maskPhoneBR(e.target.value) })} placeholder="(21) 99999-0000" className={INPUT_CLS} data-testid="plan-input-phone" />
              </div>
              <div>
                <label className={LBL_CLS}><Mail size={14} /> E-mail</label>
                <input required type="email" value={form.guest_email} onChange={(e) => update({ guest_email: e.target.value })} placeholder="seu@email.com" className={INPUT_CLS} data-testid="plan-input-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LBL_CLS}><FileText size={14} /> CPF</label>
                <input required inputMode="numeric" value={form.guest_cpf} onChange={(e) => update({ guest_cpf: maskCPF(e.target.value) })} placeholder="000.000.000-00" className={INPUT_CLS} data-testid="plan-input-cpf" />
              </div>
              <div>
                <label className={LBL_CLS}><FileText size={14} /> RG</label>
                <input required value={form.guest_rg} onChange={(e) => update({ guest_rg: maskRG(e.target.value) })} placeholder="00.000.000-0" className={INPUT_CLS} data-testid="plan-input-rg" />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700 flex items-center gap-1.5">
              <MapPin size={14} /> Endereço
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3">
              <div>
                <label className={LBL_CLS}>CEP</label>
                <div className="relative">
                  <input required inputMode="numeric" value={form.address_cep} onChange={(e) => update({ address_cep: maskCEP(e.target.value) })} onBlur={onCepBlur} placeholder="00000-000" className={INPUT_CLS} data-testid="plan-input-cep" />
                  {cepLoading && (<Loader2 size={16} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />)}
                </div>
              </div>
              <div>
                <label className={LBL_CLS}>Rua</label>
                <input required value={form.address_street} onChange={(e) => update({ address_street: e.target.value })} className={INPUT_CLS} data-testid="plan-input-street" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
              <div>
                <label className={LBL_CLS}>Número</label>
                <input required value={form.address_number} onChange={(e) => update({ address_number: e.target.value })} className={INPUT_CLS} data-testid="plan-input-number" />
              </div>
              <div>
                <label className={LBL_CLS}>Complemento</label>
                <input value={form.address_complement} onChange={(e) => update({ address_complement: e.target.value })} placeholder="Apto, bloco" className={INPUT_CLS} data-testid="plan-input-complement" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={LBL_CLS}>Bairro</label>
                <input required value={form.address_neighborhood} onChange={(e) => update({ address_neighborhood: e.target.value })} className={INPUT_CLS} data-testid="plan-input-neighborhood" />
              </div>
              <div>
                <label className={LBL_CLS}>Cidade</label>
                <input required value={form.address_city} onChange={(e) => update({ address_city: e.target.value })} className={INPUT_CLS} data-testid="plan-input-city" />
              </div>
              <div>
                <label className={LBL_CLS}>Estado</label>
                <select required value={form.address_state} onChange={(e) => update({ address_state: e.target.value })} className={INPUT_CLS} data-testid="plan-input-state">
                  <option value="">UF</option>
                  {BR_STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700">Forma de pagamento</h2>
            <div className="flex gap-2" data-testid="plan-payment-methods">
              {[["pix", "PIX"], ["cartao", "Cartão"]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setMethod(v)}
                  data-testid={`plan-method-${v}`}
                  className={`flex-1 rounded-xl border py-3 text-sm font-bold transition-all ${
                    method === v ? "border-[#0A1128] bg-[#0A1128] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}>{label}</button>
              ))}
            </div>
          </section>

          <button type="submit" disabled={loading} data-testid="plan-submit"
            className={`w-full rounded-xl py-4 font-bold uppercase tracking-wider text-sm transition-all ${
              loading ? "bg-gray-400 text-white cursor-wait" : "bg-[#0A1128] text-white hover:bg-[#14213D] active:scale-[0.99]"
            }`}>
            {loading ? "Gerando cobrança…" : method === "pix" ? "Gerar PIX e ativar plano →" : "Pagar com Cartão →"}
          </button>
        </form>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5" data-testid="plan-summary">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{plan.category || "Plano"}</div>
            <div className="font-bold text-[#0A1128] mt-1">{plan.pricing_mode === "package" ? hourBankName(plan) : plan.name}</div>
            {plan.description && (
              <p className="text-xs text-gray-600 mt-2">{plan.description}</p>
            )}
            {plan.package_hours > 0 && (
              <div className="text-xs text-gray-500 mt-2">Banco de Horas: <b>{plan.package_hours}h</b>{plan.validity_days ? ` · válido ${plan.validity_days} dias` : ""}</div>
            )}
            {(() => {
              const eco = economia(plan, spaces);
              if (!eco) return null;
              return (
                <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-0.5" data-testid="plan-economy-box">
                  <div className="text-gray-500">Valor avulso ({plan.package_hours}h × {formatBRL(eco.rate)}/h): <s>{formatBRL(eco.avulso)}</s></div>
                  <div className="text-emerald-700 font-bold text-sm">Você economiza {formatBRL(eco.save)}</div>
                  <div className="text-gray-500">Apenas {formatBRL(eco.perHour)}/hora no Banco de Horas</div>
                </div>
              );
            })()}
            <div className="border-t border-gray-200 pt-4 mt-4 flex items-baseline justify-between">
              <span className="font-semibold text-gray-600">Total</span>
              <span className="text-2xl font-black text-[#0A1128]" data-testid="plan-summary-price">{formatBRL(price)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
